import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import InfoStepper from "./InfoStepper";
import CollectionTabsWrapper from "@/components/collection/CollectionTabsWrapper";
import { iCollection, getCollection } from "@/services/collection";
import { iGroup, getGroup } from "@/services/groups";
import { useState, useEffect } from "react";
import getLink, { LINK_TYPE } from "@/lib/links";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorPage from "@/components/errors/ErrorPage";
import { t } from "i18next";
import { useSectionTitle } from "@/context/SectionTitleContext";
import { useRbac } from "@/hooks/rbac";
import axios, { AxiosError } from "axios";
import { selectUser } from "@/store/userSlice";
import { useSelector } from "react-redux";
import HotelData from "./onboarding/HotelData";
import HotelChain from "./onboarding/HotelChain";
import HotelPrice from "./onboarding/HotelPrice";
import HotelBenefits from "./onboarding/HotelBenefits";
import LoyaltySettings from "./loyalty/loyalty_settings";
import TakyonServicesRouter from "./takyon_services/takyon_services_router";
import HotelBookingEngine from "./onboarding/HotelBookingEngine";

export interface CollectionPageChildProps {
  collection?: iCollection;
  group?: iGroup;
  loading: boolean;
  onRefresh(): Promise<void>;
}

export default function CollectionPage() {
  const { collectionId } = useParams();

  const [collection, setCollection] = useState<iCollection>();

  const [group, setGroup] = useState<iGroup>();

  const { isSuperAdmin } = useRbac();

  const [isLoading, setIsLoading] = useState(true);

  const { setLogoHomeLink } = useSectionTitle();

  const navigate = useNavigate();

  const user = useSelector(selectUser);

  const load = async (id: string) => {
    if (!id) return;
    setIsLoading(true);
    try {
      const c = await getCollection(id);

      if (!c.groupId) {
        console.log("No groupId found for collection: ", c);
        navigate(getLink(LINK_TYPE.HOME));
        return;
      }
      const g = await getGroup(c.groupId);

      setCollection(c);
      setGroup(g);
    } catch (error: unknown) {
      console.log("Error loading collection:", error);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        console.log("Error details:", axiosError.response?.data);
        if (
          axiosError.response?.status === 404 ||
          axiosError.response?.status === 500
        ) {
          navigate(getLink(LINK_TYPE.HOME));
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin && collection) {
      // set logo home link to current collection group page
      setLogoHomeLink(`/group/${collection.groupId}`);
    }
  }, [collection, isSuperAdmin]);

  useEffect(() => {
    if (!user || !collectionId) return;
    load(collectionId);
  }, [collectionId, user]);

  if (isLoading)
    return <Skeleton className="bg-gray-200 h-[32px] rounded-lg" />;

  if (!collection || !group)
    return <ErrorPage message={t("error.not_found")} />;

  return (
    <Routes>
      <Route
        path="infos"
        element={<InfoStepper loading={isLoading} collection={collection} />}
      >
        <Route
          path="hoteldata"
          element={
            <HotelData
              collection={collection}
              group={group}
              onRefresh={() => load(collection._id)}
              loading={isLoading}
            />
          }
        />
        <Route
          path="hotelbookingengine"
          element={
            <HotelBookingEngine
              collection={collection}
              group={group}
              onRefresh={() => load(collection._id)}
              loading={isLoading}
            />
          }
        />
        <Route
          path="hotelprice"
          element={
            <HotelPrice
              collection={collection}
              group={group}
              onRefresh={() => load(collection._id)}
              loading={isLoading}
            />
          }
        />
        <Route
          path="hotelbenefits"
          element={
            <HotelBenefits
              collection={collection}
              group={group}
              onRefresh={() => load(collection._id)}
              loading={isLoading}
            />
          }
        />
        <Route
          path="hotelchain"
          element={
            <HotelChain
              collection={collection}
              group={group}
              loading={isLoading}
              onRefresh={() => load(collection._id)}
            />
          }
        />
      </Route>
      <Route
        path="loyalty-settings/*"
        element={
          <LoyaltySettings
            onRefresh={() => load(collection._id)}
            collection={collection}
          />
        }
      />
      <Route path="service/*" element={<TakyonServicesRouter />} />
      <Route
        path="*"
        element={
          <CollectionTabsWrapper
            collection={collection}
            group={group}
            onRefresh={() => load(collection._id)}
            loading={isLoading}
          />
        }
      ></Route>
    </Routes>
  );
}
